import { useEffect, useRef, useState, type FormEvent } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { builderStartQueryKey, getBuilderStart, startBuilderSite, type StartStatus } from "@/features/builder/api/startApi";
import { BrandMark } from "@/features/builder/components/BrandMark";
import { queryClient } from "@/lib/queryClient";
import { getDomain } from "@/utils/route-utils";
import "@/features/builder/workspace.css";

// Outside `_protected`: a new owner has no card site, and starting doesn't need one.
export const Route = createFileRoute("/start")({
  beforeLoad: ({ context, location }) => {
    if (!context.auth.session) {
      throw redirect({ to: "/auth/login", replace: true, search: { returnTo: location.href } });
    }
  },
  component: StartRoute,
});

const addressProblems = {
  invalid: "Use 4 to 63 lowercase letters, numbers or single hyphens.",
  reserved: "That address is kept for Stack.je. Try another.",
  taken: "That address is already taken. Try another.",
} as const;

/** Reserves a new owner's address, or confirms a card-site owner's existing one, before Builder. */
function StartRoute() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StartStatus | null>(null);
  const [subdomain, setSubdomain] = useState("");
  const [addressProblem, setAddressProblem] = useState<keyof typeof addressProblems | null>(null);
  const [failed, setFailed] = useState(false);
  const [starting, setStarting] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const started = useRef(-1);

  useEffect(() => {
    if (started.current === attempt) return;
    started.current = attempt;
    getBuilderStart()
      .then((next) => {
        if (!next || !next.available) return navigate({ to: "/", replace: true });
        if (next.siteId) return navigate({ to: "/builder", replace: true });
        setStatus(next);
      })
      .catch(() => setFailed(true));
  }, [attempt, navigate]);

  const begin = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!status || starting) return;
    setStarting(true);
    setFailed(false);
    setAddressProblem(null);
    try {
      const result = await startBuilderSite(status.reservedSubdomain ? undefined : subdomain.trim().toLowerCase());
      if ("refused" in result) return navigate({ to: "/", replace: true });
      if ("problem" in result) {
        setAddressProblem(result.problem);
        return;
      }
      queryClient.setQueryData(builderStartQueryKey, {
        siteId: result.siteId, available: true, cardSite: status.cardSite, reservedSubdomain: null, cardSitePublished: status.cardSitePublished,
      } satisfies StartStatus);
      return navigate({ to: "/builder", replace: true });
    } catch {
      setFailed(true);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="bw">
      <div className="bw-full">
        <div className="bw-full-card" role="status">
          <BrandMark size={40} />
          {failed ? (
            <>
              <h1>Your website couldn’t be set up</h1>
              <p>Check your connection and try again. If it was set up, trying again opens it.</p>
              <button type="button" className="bw-btn bw-btn--primary" onClick={() => {
                setFailed(false);
                if (status) void begin();
                else setAttempt((n) => n + 1);
              }}>
                Try again
              </button>
            </>
          ) : !status ? (
            <>
              <h1>Setting up your website</h1>
              <p>This takes a moment.</p>
            </>
          ) : status.reservedSubdomain ? (
            <>
              <h1>Build a new site at {status.reservedSubdomain}.{getDomain()}</h1>
              {status.cardSitePublished ? (
                <p>Stax will read your current site and build a first draft of the new one from it. Your current site stays live at this address until you publish the new site.</p>
              ) : (
                <p>Your current site stays live at this address while you build. It is replaced only when you publish the new site.</p>
              )}
              <button type="button" className="bw-btn bw-btn--primary" disabled={starting} onClick={() => void begin()}>
                {starting ? "Setting up…" : "Start building"}
              </button>
            </>
          ) : (
            <form className="bw-address-form" onSubmit={(event) => void begin(event)}>
              <h1>Choose your website address</h1>
              <p>Use your name or business name—something easy to remember. This reserves it for your site.</p>
              <label htmlFor="builder-subdomain">Your web address</label>
              <div className="bw-address-input">
                <input
                  id="builder-subdomain"
                  value={subdomain}
                  autoFocus
                  inputMode="url"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="yourbusiness"
                  aria-invalid={addressProblem ? true : undefined}
                  aria-describedby="builder-subdomain-note"
                  onChange={(event) => {
                    setSubdomain(event.target.value.toLowerCase());
                    setAddressProblem(null);
                  }}
                />
                <span>.{getDomain()}</span>
              </div>
              <p id="builder-subdomain-note" className="bw-item-note" role={addressProblem ? "alert" : undefined}>
                {addressProblem ? addressProblems[addressProblem] : "You can change it until your website first goes live."}
              </p>
              <button type="submit" className="bw-btn bw-btn--primary" disabled={!subdomain.trim() || starting}>
                {starting ? "Reserving…" : "Reserve address and start"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
